import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName, localizedField as getLocalized } from "../../utils/localizedCatalogFields";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../../utils/coachExerciseNavigation";
import { deriveCoachControlIdleTimers } from "../../coaching/coachExerciseLiveActions";
import { isTeSequences, isWarmupExercise } from "../../utils/teExecutionType";
import {
  blockTypeHeadline,
  isTypeOnlyBlock,
  plannedBlockTitle,
} from "../../utils/planningSubgroups";
import { isLiveTimerBlock } from "../../utils/teCoachTools";
import {
  useExerciseRemainingSeconds,
  useKotRoundElapsedSeconds,
  useSequenceRemainingSeconds,
} from "../../hooks/useKotHeroTimers";
import { AUTO_SEQUENCE_TRANSITION_PAUSE_SEC } from "../../utils/sequenceTransitionPause";
import { schemaKeyFromExercise, schemaLabel } from "../../components/funtionsBD/tacticalPatternService";
import { resolveLiveSequence } from "../../utils/liveTrainingSequenceResolve";
import { resolveWarmupCoachHeroTitle } from "../../utils/liveTrainingSequenceAutoeval";

const DOUBLE_TAP_MS = 420;
function formatTimer(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "--:--";
  const s = Math.max(0, Math.floor(Number(seconds)));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function getPlannedExerciseDurationSec(exercise) {
  const sequences = exercise?.sequences || [];
  if (sequences.length > 0) {
    return sequences.reduce(
      (sum, sq) => sum + (parseInt(sq.sequence_duration, 10) || 0) * 60,
      0
    );
  }
  return (parseInt(exercise?.te_duration, 10) || 0) * 60;
}

function getPlannedSequenceDurationSec(sequence) {
  if (!sequence) return 0;
  return (parseInt(sequence.sequence_duration, 10) || 0) * 60;
}

export function LiveTrainingExerciseHero({
  exercise,
  exerciseStateId,
  exerciseNumber = null,
  stationName = null,
  tableLabel,
  opponentName,
  awaitingManualSequenceStart,
  awaitingAutoSequenceStart = false,
  sequenceTransitionPauseRemaining = null,
  currentSequenceIndex = null,
  isCoachView,
  isKot = false,
  kotSnapshot = null,
  onOpenExerciseDetail = null,
  onAssignExercise = null,
  captureListening = false,
  captureVoiceState = "idle",
  onToggleCaptureListen = null,
}) {
  const { t, i18n } = useTranslation();
  const lang = currentAppLanguage(i18n);
  const lastNameTapRef = useRef(0);

  const handleExerciseNamePointer = () => {
    if (!isCoachView || typeof onOpenExerciseDetail !== "function") return;
    const now = Date.now();
    if (now - lastNameTapRef.current < DOUBLE_TAP_MS) {
      lastNameTapRef.current = 0;
      onOpenExerciseDetail(exercise);
      return;
    }
    lastNameTapRef.current = now;
  };

  const isActive =
    exerciseStateId === TE_STATE_STARTED || exerciseStateId === TE_STATE_PAUSED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const isBetweenSeq =
    (!!awaitingManualSequenceStart || !!awaitingAutoSequenceStart) &&
    isTeSequences(exercise) &&
    !isKot;

  const idleTimers =
    isCoachView && !isActive ? deriveCoachControlIdleTimers(exercise) : null;

  const kotCoachUi = kotSnapshot?.coach_ui ?? null;
  const kotRoundState = kotSnapshot?.current_round?.round_state ?? null;
  const kotBetweenRounds =
    kotRoundState === "PENDING" ||
    kotCoachUi?.awaiting_coach_start === true;
  const kotRoundClosing =
    kotCoachUi?.round_closing === true ||
    kotCoachUi?.all_tables_submitted === true;

  const hasSequences =
    isTeSequences(exercise) && !isKot && (exercise?.sequences?.length || 0) > 0;

  const roundElapsed = useKotRoundElapsedSeconds(
    isKot && !kotBetweenRounds && !kotRoundClosing ? kotSnapshot : null
  );
  const exerciseRemainingLive = useExerciseRemainingSeconds(
    exercise,
    isActive,
    isPaused
  );
  const sequenceRemainingLive = useSequenceRemainingSeconds(
    exercise,
    isActive,
    isPaused
  );

  const roundDurationSec =
    kotSnapshot?.round_timer?.round_duration_seconds != null
      ? Number(kotSnapshot.round_timer.round_duration_seconds)
      : null;
  const roundNumber = kotSnapshot?.current_round?.round_number ?? null;

  const kotModeActive =
    isKot && isActive && !isBetweenSeq && !kotBetweenRounds && !kotRoundClosing;

  const ringShowsSequenceTimer =
    hasSequences && isActive && !isBetweenSeq && !kotModeActive;

  const plannedExerciseDurationSec = getPlannedExerciseDurationSec(exercise);
  const { idx: activeSeqIdx, number: currentSeqNumber, obj: currentSeqObj } =
    resolveLiveSequence(exercise, currentSequenceIndex);
  const plannedSequenceDurationSec = getPlannedSequenceDurationSec(currentSeqObj);
  const idleSequenceDurationSec =
    idleTimers && hasSequences
      ? getPlannedSequenceDurationSec(
          exercise?.sequences?.[activeSeqIdx] || exercise?.sequences?.[0]
        )
      : 0;

  const exerciseRemainingSec = kotModeActive
    ? exerciseRemainingLive
    : isBetweenSeq && awaitingAutoSequenceStart
      ? sequenceTransitionPauseRemaining ?? 0
      : isBetweenSeq
        ? (exercise?.timer ?? 0)
        : idleTimers
          ? idleTimers.timer
          : (exerciseRemainingLive ?? exercise?.timer ?? 0);

  const rawTimer = kotModeActive
    ? roundElapsed
    : isBetweenSeq && awaitingAutoSequenceStart
      ? sequenceTransitionPauseRemaining ?? 0
      : isBetweenSeq
        ? (exerciseRemainingLive ?? exercise?.timer ?? 0)
        : idleTimers
          ? idleTimers.timer
          : ringShowsSequenceTimer
            ? (sequenceRemainingLive ?? exercise?.sequenceTimer ?? 0)
            : (exerciseRemainingLive ?? exercise?.timer ?? 0);

  const timerSec = rawTimer;

  const totalSec = kotModeActive
    ? roundDurationSec && roundDurationSec > 0
      ? roundDurationSec
      : null
    : isBetweenSeq && awaitingAutoSequenceStart
      ? AUTO_SEQUENCE_TRANSITION_PAUSE_SEC
      : idleTimers
        ? idleTimers.timer
        : isBetweenSeq
          ? plannedExerciseDurationSec || timerSec
          : ringShowsSequenceTimer
            ? plannedSequenceDurationSec || timerSec
            : plannedExerciseDurationSec || timerSec;

  const progress =
    totalSec > 0
      ? Math.min(
          100,
          Math.max(
            0,
            kotModeActive
              ? (timerSec / totalSec) * 100
              : ((totalSec - timerSec) / totalSec) * 100
          )
        )
      : 0;

  const urgent =
    kotModeActive
      ? exerciseRemainingSec != null &&
        exerciseRemainingSec > 0 &&
        exerciseRemainingSec <= 30
      : isActive &&
        !isBetweenSeq &&
        timerSec > 0 &&
        timerSec <= 30;

  const typeOnly = isTypeOnlyBlock(exercise);
  const typeHeadline = blockTypeHeadline(exercise, lang);
  const showAssignExercise =
    Boolean(isCoachView && typeOnly && typeof onAssignExercise === "function");
  const timerOnly = isLiveTimerBlock(exercise);
  const exName = timerOnly
    ? t("liveTrainingTimerBlock", "Timer")
    : localizedExerciseName(exercise, lang) ||
      plannedBlockTitle(exercise, lang) ||
      getLocalized(exercise?.type_ex, "type_name", lang) ||
      t("exercise");
  const order = exerciseNumber ?? exercise?.te_order ?? null;
  const sequenceInProgress =
    hasSequences && isActive && !isBetweenSeq;
  const warmupCoachHero =
    isCoachView && hasSequences && isWarmupExercise(exercise);
  const warmupSeqTitle = warmupCoachHero
    ? resolveWarmupCoachHeroTitle(exercise, {
        lang,
        sequenceInProgress,
        awaitingManualSequenceStart,
        awaitingAutoSequenceStart,
      })
    : null;
  const heroName =
    warmupSeqTitle?.name ||
    (warmupCoachHero && warmupSeqTitle?.order != null
      ? `${t("liveTrainingSequence")} ${warmupSeqTitle.order}`
      : exName);
  const heroOrder =
    warmupCoachHero && warmupSeqTitle?.order != null
      ? warmupSeqTitle.order
      : order;
  const seqTotal = exercise?.sequences?.length || 0;

  const stateClass = [
    urgent ? "lt-hero--urgent" : "",
    isPaused ? "lt-hero--paused" : "",
    isBetweenSeq ? "lt-hero--between-seq" : "",
    !isActive && !isBetweenSeq ? "lt-hero--idle" : "",
    kotModeActive ? " lt-hero--kot-round" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const timerStateLabel = kotModeActive ? (
    roundNumber != null ? (
      t("kotRoundNumberLabel", {
        n: roundNumber,
        defaultValue: `Round #${roundNumber}`,
      })
    ) : (
      t("kotRoundElapsed")
    )
  ) : isBetweenSeq && awaitingAutoSequenceStart ? (
    t("sequenceTransitionPause") || "Next sequence in"
  ) : isBetweenSeq ? (
    t("exercise")
  ) : isPaused ? (
    t("paused")
  ) : ringShowsSequenceTimer && currentSeqNumber != null ? (
    `${t("liveTrainingSequence")} ${currentSeqNumber}`
  ) : isActive ? (
    t("live")
  ) : isCoachView ? (
    t("liveTrainingReady")
  ) : null;

  const showExerciseRemaining =
    exerciseRemainingSec != null &&
    Number.isFinite(Number(exerciseRemainingSec)) &&
    (kotModeActive || ringShowsSequenceTimer);

  const sequenceRemainingSec =
    isCoachView && !isActive && hasSequences
      ? idleSequenceDurationSec || idleTimers?.sequenceTimer || null
      : sequenceRemainingLive ?? exercise?.sequenceTimer ?? null;

  const showSequenceRemaining =
    isCoachView &&
    !isActive &&
    !isBetweenSeq &&
    hasSequences &&
    sequenceRemainingSec != null &&
    Number.isFinite(Number(sequenceRemainingSec)) &&
    Number(sequenceRemainingSec) > 0;

  const secondaryTimer = showExerciseRemaining
    ? {
        label: t("exercise_remaining_time"),
        value: exerciseRemainingSec,
        urgent:
          !kotModeActive &&
          exerciseRemainingSec > 0 &&
          exerciseRemainingSec <= 30,
      }
    : showSequenceRemaining
      ? {
          label: t("sequence_remaining_time"),
          value: sequenceRemainingSec,
          urgent: false,
        }
      : null;

  return (
    <div className={`lt-hero ${stateClass}`} data-tour-id="live-player-exercise">
      {isBetweenSeq ? (
        <div className="lt-hero__seq-banner">
          {currentSeqNumber != null
            ? `${t("liveTrainingSeqComplete")} · ${t("liveTrainingSequence")} ${currentSeqNumber}`
            : t("liveTrainingSeqComplete")}
        </div>
      ) : null}

      <div className="lt-hero__ring-wrap">
        <svg className="lt-hero__ring" viewBox="0 0 120 120" aria-hidden>
          <circle className="lt-hero__ring-bg" cx="60" cy="60" r="52" />
          <circle
            className="lt-hero__ring-progress"
            cx="60"
            cy="60"
            r="52"
            style={{
              strokeDasharray: 326.7,
              strokeDashoffset: 326.7 - (326.7 * progress) / 100,
            }}
          />
        </svg>
        <div className="lt-hero__timer" aria-live="polite">
          <span className="lt-hero__timer-value">{formatTimer(timerSec)}</span>
          {timerStateLabel ? (
            <span
              className={`lt-hero__timer-state${
                isBetweenSeq ? " lt-hero__timer-state--ex" : ""
              }${!isActive && !isBetweenSeq && isCoachView ? " lt-hero__timer-state--idle" : ""}`}
            >
              {timerStateLabel}
            </span>
          ) : null}
        </div>
      </div>

      {secondaryTimer ? (
        <div
          className={`lt-hero__exercise-remaining${
            secondaryTimer.urgent || (urgent && showExerciseRemaining)
              ? " lt-hero__exercise-remaining--urgent"
              : ""
          }`}
          aria-live="polite"
        >
          <span className="lt-hero__exercise-remaining-label">
            {secondaryTimer.label}
          </span>
          <span className="lt-hero__exercise-remaining-value">
            {formatTimer(secondaryTimer.value)}
          </span>
        </div>
      ) : null}

      {stationName ? (
        <p className="lt-hero__station">{stationName}</p>
      ) : null}

      {showAssignExercise ? (
        <div className="lt-hero__type-row">
          {heroOrder != null ? (
            <span className="lt-hero__number">{heroOrder}.</span>
          ) : null}
          <span className="lt-hero__type">
            {typeHeadline || heroName || t("trainingBlock") || "Training block"}
          </span>
          <button
            type="button"
            className="lt-hero__assign"
            onClick={() => onAssignExercise?.(exercise)}
          >
            {t("selectTraining") || "Select training"}
          </button>
        </div>
      ) : (
      <h2
        className={`lt-hero__name${
          isCoachView && onOpenExerciseDetail ? " lt-hero__name--coach-tap" : ""
        }`}
        onClick={
          isCoachView && onOpenExerciseDetail
            ? handleExerciseNamePointer
            : undefined
        }
        onDoubleClick={
          isCoachView && onOpenExerciseDetail
            ? (e) => {
                e.preventDefault();
                lastNameTapRef.current = 0;
                onOpenExerciseDetail(exercise);
              }
            : undefined
        }
        title={
          isCoachView && onOpenExerciseDetail
            ? t(
                "liveTrainingOpenExerciseDetailHint",
                "Double-tap to open exercise details"
              )
            : undefined
        }
        role={isCoachView && onOpenExerciseDetail ? "button" : undefined}
        tabIndex={isCoachView && onOpenExerciseDetail ? 0 : undefined}
        onKeyDown={
          isCoachView && onOpenExerciseDetail
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpenExerciseDetail(exercise);
                }
              }
            : undefined
        }
      >
        {heroOrder != null ? (
          <span className="lt-hero__number">{heroOrder}.</span>
        ) : null}
        {heroName}
      </h2>
      )}
      {exercise?.details_pending ? (
        <p className="lt-hero__pending">
          {t("liveTrainingDetailsPending", "Exercise details pending")}
        </p>
      ) : null}
      {isCoachView && exercise?.capture_status && typeof onToggleCaptureListen === "function" ? (
        <button
          type="button"
          className={`lt-hero__listen${captureListening ? " is-on" : ""}`}
          disabled={
            captureVoiceState === "unsupported" || captureVoiceState === "denied"
          }
          onClick={onToggleCaptureListen}
        >
          {captureVoiceState === "unsupported" || captureVoiceState === "denied"
            ? t(
                "liveTrainingPingfloUnavailable",
                "Pingflo cannot use the microphone in this browser"
              )
            : captureListening
              ? t("liveTrainingPingfloListening", "Pingflo listening")
              : t("liveTrainingPingfloPaused", "Pingflo paused")}
        </button>
      ) : null}
      {schemaKeyFromExercise(exercise) ? (
        <p className="lt-hero__schema">
          {schemaLabel(schemaKeyFromExercise(exercise), lang)}
        </p>
      ) : null}

      {seqTotal > 0 ? (
        <div className="lt-hero__sequences">
          {Array.from({ length: seqTotal }).map((_, i) => (
            <span
              key={i}
              className={`lt-hero__seq-pill${
                i === activeSeqIdx ? " lt-hero__seq-pill--active" : ""
              }${i < activeSeqIdx ? " lt-hero__seq-pill--done" : ""}`}
            >
              {i + 1}
            </span>
          ))}
        </div>
      ) : null}

      {(tableLabel || opponentName) && (
        <div className="lt-hero__matchup">
          {tableLabel ? (
            <span className="lt-hero__table">{tableLabel}</span>
          ) : null}
          {opponentName ? (
            <span className="lt-hero__vs">
              {t("vs")} {opponentName}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default LiveTrainingExerciseHero;
